import {
  forwardRef,
  useState,
  FunctionComponent,
  KeyboardEvent,
  MouseEventHandler,
  MouseEvent,
  UIEvent,
  PropsWithoutRef,
  useCallback,
  KeyboardEventHandler
} from 'react';
import styled from 'styled-components';

import { BaseProps, ForwardProps, NoChildrenProp, OmitStrict } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useConsolidatedRef, useUID, useFocusWithin, useDirection } from '../../hooks';
import Button, { ButtonProps, StyledButton } from '../Button';
import Popover, { PopoverProps, StyledPopover } from '../Popover';
import Menu from '../Menu';
import { AcceptedMouseEventElement } from '../Menu/Menu.types';
import Icon, { registerIcon } from '../Icon';
import * as arrowMicroDownIcon from '../Icon/icons/arrow-micro-down.icon';
import Flex from '../Flex';
import Text from '../Text';
import { Count } from '../Badges';
import type { MenuProps } from '../Menu/Menu.types';
import type { CountProps } from '../Badges/Count';

registerIcon(arrowMicroDownIcon);

export interface MenuButtonProps extends BaseProps, NoChildrenProp {
  /** The text for the MenuButton also used to set the aria-label. */
  text: string;
  /**
   * Determines the Button variant for the MenuButton.
   * @default "secondary"
   */
  variant?: ButtonProps['variant'];
  /** The name of the icon for the MenuButton. */
  icon?: string;
  /** Only display the icon. The text prop will be forwarded to a tooltip. */
  iconOnly?: ButtonProps['icon'];
  /** The total count representing the list data which was selected. */
  count?: CountProps['children'];
  /** The click handler for the MenuButton. */
  onClick?: MouseEventHandler;
  /** Menu related props. */
  menu?: MenuProps;
  /** Popover related props. */
  popover?: OmitStrict<PopoverProps, 'show' | 'target' | 'children'>;
  /** DOM id for the button element. */
  id?: string;
  /** Ref for the button element. */
  ref?: ButtonProps['ref'];
  /** The keydown handler for the MenuButton. */
  onKeyDown?: KeyboardEventHandler;
}

const StyledMenuButton = styled.button`
  flex-shrink: 0;

  & + ${StyledPopover} + ${StyledButton} {
    margin-inline-start: ${props => props.theme.base.spacing};
  }
`;

StyledMenuButton.defaultProps = defaultThemeProp;

const StyledMenuButtonPopover = styled(Popover)`
  min-width: 20ch;
`;

const MenuButton: FunctionComponent<ForwardProps & MenuButtonProps> = forwardRef(
  (props: PropsWithoutRef<MenuButtonProps>, ref: MenuButtonProps['ref']) => {
    const uid = useUID();
    const {
      id = uid,
      text,
      menu,
      popover,
      onClick,
      onKeyDown,
      icon,
      count,
      iconOnly = false,
      ...restProps
    } = props;

    const [isOpen, setIsOpen] = useState(false);

    const buttonRef = useConsolidatedRef(ref);
    const popoverRef = useConsolidatedRef<HTMLDivElement>(popover?.ref);
    const menuRef = useConsolidatedRef<HTMLDivElement>(menu?.ref);

    // FIXME: Type assertion required for issue in useFocusWithin generic
    useFocusWithin<HTMLElement>(
      [popoverRef, buttonRef],
      useCallback(isFocused => {
        if (!isFocused) setIsOpen(false);
      }, [])
    );

    const { rtl } = useDirection();

    return (
      <>
        <Button
          as={StyledMenuButton}
          {...restProps}
          id={id}
          ref={buttonRef}
          aria-expanded={isOpen}
          aria-haspopup='menu'
          aria-controls={`${id}-popover`}
          aria-label={text}
          label={iconOnly && !isOpen ? text : undefined}
          onClick={(e: MouseEvent<HTMLButtonElement> & UIEvent<HTMLButtonElement>) => {
            // clickCount is 0 when triggered by keyboard.
            const clickCount = e.detail;

            // Close the menu if it is open and either:
            //   It is a *mouse* click on the button.
            //   Or, the menu mode is not multiselect.
            if (isOpen && (clickCount > 0 || menu?.mode !== 'multi-select')) {
              setIsOpen(false);
            } else {
              setIsOpen(true);
            }

            onClick?.(e);
          }}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
            onKeyDown?.(e);
          }}
          icon={iconOnly}
        >
          <Flex container={{ alignItems: 'center', gap: 1 }}>
            {icon && <Icon name={icon} />}
            {!iconOnly && (
              <Text>
                {text} {count !== undefined && <Count>{count}</Count>}
                {text && <Icon name='arrow-micro-down' />}
              </Text>
            )}
          </Flex>
        </Button>
        <StyledMenuButtonPopover
          placement={rtl ? 'bottom-end' : 'bottom-start'}
          id={`${id}-popover`}
          {...popover}
          hideOnTargetHidden
          show={!!menu && isOpen}
          target={buttonRef.current}
          ref={popoverRef}
        >
          {menu && (
            <Menu
              {...menu}
              ref={menuRef}
              items={menu.items}
              onItemClick={(itemId, e) => {
                if (
                  menu.mode !== 'multi-select' &&
                  (e as MouseEvent<AcceptedMouseEventElement> & UIEvent<AcceptedMouseEventElement>)
                    .detail > 0
                ) {
                  setIsOpen(false);
                }
                menu.onItemClick?.(itemId, e);
              }}
              focusControlEl={buttonRef.current || undefined}
            />
          )}
        </StyledMenuButtonPopover>
      </>
    );
  }
);

export default MenuButton;
